import {Sprite} from 'pixi.js';
import {Spine} from '@gongxigames/framework/core/fragments/display/Spine';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';
import {AUTOMATION_META, AutomationMeta} from '@gongxigames/framework/core/fragments/Autotest';

export enum SpineButtonState {
    UP,
    DOWN,
    OVER,
    DISABLED
}

export class SpineButton extends Sprite {
    public static readonly ON_STATE_CHANGED: symbol = Symbol('ON_STATE_CHANGED');

    protected _spine!: Spine;
    protected _clicked = false;

    protected _state = SpineButtonState.UP;
    protected _over = false;
    protected _clickCallback?: Function;
    protected _clickActionCompleteCallback?: Function;
    protected hoverStartAnimationName!: string;
    protected hoverEndAnimationName!: string;
    protected idleAnimationName?: string;
    protected clickAnimationName!: string;
    protected trackIndex!: number;

    constructor(clickCallback?: Function) {
        super();

        this._clickCallback = clickCallback;
        this.enable();
    }

    public setUpSpine(spine: Spine,
        hoverStartAnimationName: string,
        hoverEndAnimationName: string,
        clickAnimationName: string,
        idleAnimationName?: string,
        trackIndex?: number): void {
        this._spine = spine;
        this.hoverStartAnimationName = hoverStartAnimationName;
        this.hoverEndAnimationName = hoverEndAnimationName;
        this.clickAnimationName = clickAnimationName;
        this.idleAnimationName = idleAnimationName;
        this.trackIndex = trackIndex ?? 0;

        this.addChild(this._spine);
    }

    public init(): void {
        if (this._spine.state.hasAnimation(this.clickAnimationName)) {
            this._spine.state.setAnimation(this.trackIndex, this.clickAnimationName, false);
        }

        this._spine.start();
        this._spine.update(0);
    }

    public enable(): void {
        if (this._over) {
            this.state = SpineButtonState.OVER;
        } else {
            this.state = SpineButtonState.UP;
        }

        this.interactive = true;
        this.buttonMode = true;
        this.addButtonEvents();
    }

    public disable(): void {
        this.state = SpineButtonState.DISABLED;
        this.interactive = false;
        this.buttonMode = false;
        this.removeButtonEvents();
    }

    public idle(loop: boolean): void {
        if (this.idleAnimationName) {
            this.playAnimation(this.idleAnimationName, loop);
        }
    }

    protected addButtonEvents(): void {
        this.on('mousedown', this.onMouseDown, this);
        this.on('mouseup', this.onMouseUp, this);
        this.on('mouseover', this.onMouseOver, this);
        this.on('mouseout', this.onMouseOut, this);
        this.on('click', this.onClick, this);

        this.on('tap', this.onClick, this);
        this.on('touchstart', this.onMouseDown, this);
        this.on('touchendoutside', this.onMouseUp, this);
        this.on('touchend', this.onMouseUp, this);
        this.on('touchendoutside', this.onMouseUp, this);
    }

    protected removeButtonEvents(): void {
        this.off('mousedown', this.onMouseDown, this);
        this.off('mouseup', this.onMouseUp, this);
        this.off('mouseover', this.onMouseOver, this);
        this.off('mouseout', this.onMouseOut, this);
        this.off('click', this.onClick, this);

        this.off('tap', this.onClick, this);
        this.off('touchstart', this.onMouseDown, this);
        this.off('touchendoutside', this.onMouseUp, this);
        this.off('touchend', this.onMouseUp, this);
        this.off('touchendoutside', this.onMouseUp, this);
    }

    protected onMouseUp(): void {
        this.state = SpineButtonState.UP;
    }

    protected onMouseDown(): void {
        this.state = SpineButtonState.DOWN;
    }

    protected onMouseOver(): void {
        if (!this._clicked) {
            this._over = true;
            this.state = SpineButtonState.OVER;
            this.playAnimation(this.hoverStartAnimationName);
        }
    }

    protected onMouseOut(): void {
        this._over = false;
        this.state = SpineButtonState.UP;
        this.playAnimation(this.hoverEndAnimationName);
    }

    protected onClick(): void {
        this._clicked = true;
        this.state = SpineButtonState.OVER;
        this.playAnimation(this.clickAnimationName, false, this._clickActionCompleteCallback);

        if (this._clickCallback) {
            this._clickCallback();
        } else {
            consoleLogger.warn('There is no clickCallback!');
        }
    }

    protected playAnimation(animationName: string, loop = false, callBack: Function = () => {}): void {
        if (this._spine.state.hasAnimation(animationName)) {
            this._spine.state.clearListeners();

            this._spine.state.setAnimation(this.trackIndex, animationName, loop);
            this._spine.state.addListener({
                complete: () => {
                    callBack();
                }
            });
        } else {
            callBack();
        }
    }

    public get state(): SpineButtonState {
        return this._state;
    }

    public set state(value: SpineButtonState) {
        if (this._state !== value) {
            this._state = value;

            this.emit(SpineButton.ON_STATE_CHANGED);
        }
    }

    public set clickCallback(value: Function) {
        this._clickCallback = value;
    }

    public set clickActionCompleteCallback(value: Function) {
        this._clickActionCompleteCallback = value;
    }

    public get spine(): Spine {
        return this._spine;
    }

    [AUTOMATION_META](): AutomationMeta {
        return {
            type: 'button',
            disabled: this.interactive && this.buttonMode,
            over: this._over,
            pressed: this.state === SpineButtonState.DOWN
        };
    }
}
